/**
 * The dashboard search box (COL-533).
 *
 * A 300px pill in the tab strip with a results dropdown, as the export draws
 * it. The index is static and lives in `tenant-dashboard-search` — it
 * describes SCREENS, not rows, so there is nothing to keep in sync with the
 * database and no query behind this at all. A result opens its tab and,
 * when it has one, the block on that tab it is about (COL-623).
 */

import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor } from "@/lib/i18n/languages";
import {
  APPLICANT_SEARCH_INDEX,
  searchTenantDashboard,
  TENANT_SEARCH_INDEX,
  type SearchTarget,
} from "@/lib/tenant-dashboard-search";
import type { DashboardV2Tab } from "@/lib/tenant-dashboard-tabs";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";

type Props = {
  /** Which index to search, and which placeholder to show. */
  applicant: boolean;
  /** Tabs this tenant can open; results on any other tab are not offered. */
  availableTabs: readonly DashboardV2Tab[];
  /**
   * Opening a result selects its tab, exactly as a `?tab=` link would, and
   * passes the block on it to scroll to.
   */
  onOpen: (tab: DashboardV2Tab, target?: SearchTarget) => void;
};

export default function TenantDashboardSearch({
  applicant,
  availableTabs,
  onOpen,
}: Props) {
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const d = translateDictionary(
    "tenant.dashboardV2",
    getTenantDashboardV2Labels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const results = searchTenantDashboard(
    query,
    applicant ? APPLICANT_SEARCH_INDEX : TENANT_SEARCH_INDEX,
    d,
    availableTabs
  );
  const showDropdown = open && query.trim().length > 0;

  /*
    Close on an outside click and on Escape.

    `mousedown`, not `click`: a result is a <button>, and closing on click
    would fire before its own handler and swallow the selection.
  */
  useEffect(() => {
    if (!showDropdown) return;
    const onDown = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [showDropdown]);

  const choose = (tab: DashboardV2Tab, target?: SearchTarget) => {
    onOpen(tab, target);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative w-full sm:w-[300px]">
      <div className="flex h-[42px] items-center gap-2.5 rounded-full border border-border/70 bg-background px-4">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={
            applicant ? d.searchPlaceholderApplicant : d.searchPlaceholderTenant
          }
          aria-label={d.searchLabel}
          className="h-full min-w-0 flex-1 border-0 bg-transparent text-[14.5px] text-foreground outline-none placeholder:text-muted-foreground"
        />
      </div>

      {showDropdown ? (
        <div className="absolute inset-x-0 top-[50px] z-20 flex flex-col rounded-2xl border border-border/70 bg-background p-1.5 shadow-[0_16px_40px_rgba(9,46,44,0.12)]">
          {results.length > 0 ? (
            results.map((entry) => (
              <button
                key={`${entry.title}-${entry.where}`}
                type="button"
                onClick={() => choose(entry.tab, entry.target)}
                className="flex flex-col items-start gap-0.5 rounded-[10px] px-3 py-2.5 text-left transition-colors hover:bg-muted"
              >
                <span className="text-[14.5px] font-semibold text-foreground">
                  {d[entry.title]}
                </span>
                <span className="text-[13px] text-muted-foreground">
                  {d[entry.where]}
                </span>
              </button>
            ))
          ) : (
            <span className="px-3 py-2.5 text-sm text-muted-foreground">
              {d.searchNothingFound}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}
